import { Button, HSpacer, Text, VSpacer } from '@/components/DesignSystem';
import { ApiRetailer } from '@api/interfaces';
import Add from '@mui/icons-material/Add';
import { Stack } from '@mui/material';
import React, { useMemo, useState } from 'react';
import { CategoryMappingModal } from './CategoryMappingModal';
import { ApiCategoryMapping } from '@api/interfaces/ApiCategoryMapping';
import {
  CategoryMappingTable,
} from '@/pages/Admin/HierarchyOfRetailers/CategoryMapping/CategoryMappingTable';
import { ApiRewardsProgram } from '@api/interfaces/ApiRewardsProgram';
import { RewardsProgramType } from '@shared/enums';

interface CategoryMappingCardProps {
  isJobProcessing?: boolean,
  mappings?: ApiCategoryMapping[],
  // Lists and creates the mappings for this kind of program only.
  programType: RewardsProgramType,
  programs?: ApiRewardsProgram[],
  retailer: ApiRetailer,
}

export const CategoryMappingCard = ({
  isJobProcessing = false,
  mappings,
  programType,
  programs,
  retailer,
}: CategoryMappingCardProps) => {
  const programMappings = useMemo(() => (
    mappings?.filter((mapping) => mapping.programType === programType) ?? []
  ), [mappings, programType]);

  const [showCategoryMappingModal, setShowCategoryMappingModal] = useState(false);
  const [selectedMappingId, setSelectedMappingId] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isDuplicating, setIsDuplicating] = useState(false);

  const selectedMapping = useMemo(() => (
    programMappings.find((mapping) => mapping.id === selectedMappingId)
  ), [programMappings, selectedMappingId]);

  return (
    <>
      <Stack>
        <Stack alignItems="center" direction="row" justifyContent="space-between">
          <Text category="title-small">
            CATEGORY MAPPINGS
          </Text>
          <Stack alignItems="center" direction="row" justifyContent="space-between">
            <HSpacer size="3" />
            <Button
              color="primary"
              onClick={() => {
                setSelectedMappingId(null);
                setIsEditing(true);
                setShowCategoryMappingModal(true);
              }}
              startIcon={<Add />}
              testID={`category-mapping-button-${programType}`}
              variant="outlined"
            >
              Add
            </Button>
          </Stack>
        </Stack>
        {!!programMappings.length && (
          <>
            <VSpacer size="4" />
            <CategoryMappingTable
              isJobProcessing={isJobProcessing}
              mappings={programMappings}
              onDuplicate={(id) => {
                setIsEditing(true);
                setIsDuplicating(true);
                setSelectedMappingId(id);
                setShowCategoryMappingModal(true);
              }}
              onEdit={(id) => {
                setIsEditing(true);
                setSelectedMappingId(id);
                setShowCategoryMappingModal(true);
              }}
              onView={(id) => {
                setIsEditing(false);
                setSelectedMappingId(id);
                setShowCategoryMappingModal(true);
              }}
              programs={programs}
            />
          </>
        )}
      </Stack>
      {showCategoryMappingModal && (
        <CategoryMappingModal
          categoryMapping={(isDuplicating ? {
            ...selectedMapping,
            name: '',
            programId: null,
          } : selectedMapping) as ApiCategoryMapping | undefined}
          existingCategoryMappings={mappings ?? []}
          isDuplicating={isDuplicating}
          isEditing={isEditing}
          onClose={() => {
            setShowCategoryMappingModal(false);
            setIsEditing(false);
            setIsDuplicating(false);
            setSelectedMappingId(null);
          }}
          programType={programType}
          programs={programs}
          retailerId={retailer.id}
        />
      )}
    </>
  );
};
